import { DonorStatus, useGetRecruiterDashboardMetricsQuery } from 'gql/types';
import { useMemo } from 'react';

import { getDateRangeBounds } from '../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import { DateRangeType } from '../types/phlebotomist-dashboard';

export interface IUseRecruiterDashboardMetricsProps {
  dateRange: DateRangeType;
  customDateRange?: { start: Date; end: Date };
  regionIds?: string[];
  officeIds?: string[];
  locationIds?: string[];
  activeDonorStatuses?: DonorStatus[];
  repeatDonationsThreshold?: number;
  donorDonationFrequency?: number;
  donorEligibilityDays?: number;
  lapsedDonorBreakpoints?: number[];
  retiredDonorBreakpoints?: number[];
  lastDonationActiveTimeoutDays?: number;
  skip?: boolean;
}

export interface IRecruiterDashboardMetrics {
  /** Program Retention Score: (repeat / active) * 100 */
  retentionScore: number;
  repeatDonorCount: number;
  activeDonorCount: number;
  historicalActiveDonorCount?: number;
  comparedPeriodActiveDonorCount?: number;

  /** Quarterly Dropout Rate: (dropouts / activeAtStart) * 100 */
  quarterlyDropoutRate: number;
  quarterlyDropoutCount: number;
  quarterlyActiveAtStart: number;

  /** First-Time vs Repeat Ratio (GCD-reduced) */
  ratioFirstTime: number;
  ratioRepeat: number;

  /** Period Churn Rate: ((dropouts - enrollments) / activeAtStart) * 100 */
  periodChurnRate: number;
  periodDropoutCount: number;
  periodNewEnrollmentCount: number;
  periodActiveAtStart: number;

  /** Quarterly Churn Rate */
  quarterlyChurnRate: number;
  quarterlyNewEnrollmentCount: number;

  /** Donor Show Rate */
  showRate: number;
  donorsWhoDonated: number;
  expectedDonations: number;

  /** Single vs Double Unit Donors */
  singleDonorCount: number;
  doubleDonorCount: number;

  /** Lapsed donor counts per breakpoint */
  lapsedDonorCounts: number[];

  /** Retired (Unresponsive) donor counts per breakpoint range */
  retiredDonorCounts: number[];
}

export interface IUseRecruiterDashboardMetricsResult {
  metrics: IRecruiterDashboardMetrics | null;
  loading: boolean;
  error?: Error;
}

/** Greatest Common Divisor for ratio reduction */
/**
 * Simplify a firstTime:repeat ratio to a human-friendly form.
 *
 * Algorithm:
 * 1. Compute firstTimePct = firstTime / (firstTime + repeat)
 * 2. For each candidate dividend d in [1, 2, 3, 5]:
 *      exactDivisor = d / firstTimePct  (the exact non-integer divisor)
 *      roundedDivisor = round(exactDivisor)
 *      roundingError = |exactDivisor - roundedDivisor|
 * 3. Pick the dividend whose divisor requires the least rounding.
 *    If a dividend in [1,2,3] is within 0.1 rounding error of
 *    the best error from dividend=5, favor the smaller dividend.
 */
function snapRatio(firstTime: number, repeat: number): [number, number] {
  if (firstTime === 0 && repeat === 0) return [0, 0];
  if (firstTime === 0) return [0, 1];
  if (repeat === 0) return [1, 0];

  const total = firstTime + repeat;
  const pct = firstTime / total; // e.g. 0.28

  const dividends = [1, 2, 3, 5];
  let bestDividend = 1;
  let bestDivisor = Math.round(1 / pct);
  let bestError = Infinity;

  for (const d of dividends) {
    const exactDivisor = d / pct;
    const rounded = Math.round(exactDivisor);
    if (rounded < 1) continue;
    const error = Math.abs(exactDivisor - rounded);

    if (error < bestError) {
      bestError = error;
      bestDividend = d;
      bestDivisor = rounded;
    }
  }

  // Second pass: if dividend=5 won, check if any [1,2,3] is within 0.1
  if (bestDividend === 5) {
    for (const d of [1, 2, 3]) {
      const exactDivisor = d / pct;
      const rounded = Math.round(exactDivisor);
      if (rounded < 1) continue;
      const error = Math.abs(exactDivisor - rounded);
      if (error - bestError <= 0.1) {
        bestDividend = d;
        bestDivisor = rounded;
        bestError = error;
        break;
      }
    }
  }

  return [bestDividend, bestDivisor];
}

function gcd(x: number, y: number): number {
  let a = Math.abs(x);
  let b = Math.abs(y);
  while (b) {
    [a, b] = [b, a % b];
  }
  return a || 1;
}

/** Get the start of a calendar quarter */
function getStartOfQuarter(date: Date): Date {
  const quarter = Math.floor(date.getMonth() / 3);
  return new Date(date.getFullYear(), quarter * 3, 1, 0, 0, 0, 0);
}

export function useRecruiterDashboardMetrics(
  props: IUseRecruiterDashboardMetricsProps
): IUseRecruiterDashboardMetricsResult {
  const {
    dateRange,
    customDateRange,
    regionIds,
    officeIds,
    locationIds,
    activeDonorStatuses,
    repeatDonationsThreshold,
    donorDonationFrequency,
    donorEligibilityDays,
    lapsedDonorBreakpoints,
    retiredDonorBreakpoints,
    lastDonationActiveTimeoutDays,
  } = props;

  // Compute period range from current date range filter
  const periodRange = useMemo(
    () => getDateRangeBounds(dateRange, customDateRange),
    [
      dateRange,
      customDateRange?.start?.getTime(),
      customDateRange?.end?.getTime(),
    ]
  );

  // Compute compared (previous) period range — same duration, shifted back
  const comparedPeriodRange = useMemo(() => {
    const durationMs = periodRange.end.getTime() - periodRange.start.getTime();
    const prevEnd = new Date(periodRange.start.getTime() - 1); // 1ms before period start
    prevEnd.setHours(23, 59, 59, 999);
    const prevStart = new Date(prevEnd.getTime() - durationMs);
    prevStart.setHours(0, 0, 0, 0);
    return { start: prevStart, end: prevEnd };
  }, [periodRange]);

  // Compute quarterly range (always current calendar quarter)
  const quarterRange = useMemo(() => {
    const now = new Date();
    return {
      start: getStartOfQuarter(now),
      end: now,
    };
  }, []);

  const { data, loading, error } = useGetRecruiterDashboardMetricsQuery({
    skip: props.skip,
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        periodRange: {
          start: periodRange.start.toISOString(),
          end: periodRange.end.toISOString(),
        },
        quarterRange: {
          start: quarterRange.start.toISOString(),
          end: quarterRange.end.toISOString(),
        },
        comparedPeriodRange: {
          start: comparedPeriodRange.start.toISOString(),
          end: comparedPeriodRange.end.toISOString(),
        },
        regionIds: regionIds?.length ? regionIds : undefined,
        officeIds: officeIds?.length ? officeIds : undefined,
        locationIds: locationIds?.length ? locationIds : undefined,
        activeDonorStatuses: activeDonorStatuses?.length
          ? activeDonorStatuses
          : undefined,
        repeatDonationsThreshold: repeatDonationsThreshold ?? undefined,
        donorDonationFrequency: donorDonationFrequency ?? undefined,
        donorEligibilityDays: donorEligibilityDays ?? undefined,
        lapsedDonorBreakpoints: lapsedDonorBreakpoints?.length
          ? lapsedDonorBreakpoints
          : undefined,
        retiredDonorBreakpoints: retiredDonorBreakpoints?.length
          ? retiredDonorBreakpoints
          : undefined,
        lastDonationActiveTimeoutDays:
          lastDonationActiveTimeoutDays ?? undefined,
      },
    },
  });

  const metrics: IRecruiterDashboardMetrics | null = useMemo(() => {
    const d = data?.getRecruiterDashboardMetrics;
    if (!d) return null;

    // Retention score
    const retentionScore =
      d.activeDonorCount > 0
        ? Math.round((d.repeatDonorCount / d.activeDonorCount) * 1000) / 10
        : 0;

    // Quarterly dropout rate
    const quarterlyDropoutRate =
      d.quarterlyActiveAtStart > 0
        ? Math.round(
            (d.quarterlyDropoutCount / d.quarterlyActiveAtStart) * 1000
          ) / 10
        : 0;

    // First-Time vs Repeat ratio (snapped to nearest simple ratio)
    const firstTime = d.firstTimeDonorCount;
    const repeat = d.repeatDonorCount;
    const [ratioFirstTime, ratioRepeat] = snapRatio(firstTime, repeat);

    // Period churn rate
    const periodChurnRate =
      d.periodActiveAtStart > 0
        ? Math.round(
            ((d.periodDropoutCount - d.periodNewEnrollmentCount) /
              d.periodActiveAtStart) *
              1000
          ) / 10
        : 0;

    // Quarterly churn rate
    const quarterlyChurnRate =
      d.quarterlyActiveAtStart > 0
        ? Math.round(
            ((d.quarterlyDropoutCount - d.quarterlyNewEnrollmentCount) /
              d.quarterlyActiveAtStart) *
              1000
          ) / 10
        : 0;

    return {
      retentionScore,
      repeatDonorCount: d.repeatDonorCount,
      activeDonorCount: d.activeDonorCount,
      historicalActiveDonorCount: d.historicalActiveDonorCount ?? undefined,
      comparedPeriodActiveDonorCount:
        d.comparedPeriodActiveDonorCount ?? undefined,
      quarterlyDropoutRate,
      quarterlyDropoutCount: d.quarterlyDropoutCount,
      quarterlyActiveAtStart: d.quarterlyActiveAtStart,
      ratioFirstTime,
      ratioRepeat,
      periodChurnRate,
      periodDropoutCount: d.periodDropoutCount,
      periodNewEnrollmentCount: d.periodNewEnrollmentCount,
      periodActiveAtStart: d.periodActiveAtStart,
      quarterlyChurnRate,
      quarterlyNewEnrollmentCount: d.quarterlyNewEnrollmentCount,
      showRate: d.showRate,
      donorsWhoDonated: d.donorsWhoDonated,
      expectedDonations: d.expectedDonations,
      singleDonorCount: d.singleDonorCount,
      doubleDonorCount: d.doubleDonorCount,
      lapsedDonorCounts: d.lapsedDonorCounts ?? [],
      retiredDonorCounts: d.retiredDonorCounts ?? [],
    };
  }, [data]);

  return {
    metrics,
    loading,
    error: error as Error | undefined,
  };
}
